// My Dashboard company work (11c §7): check-ins, My tasks, Team pool — one data hook, three movable modules.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserCheck, ListChecks, Hand, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { workspaceApi, withMe, dueInfo, TASK_CHANGED, emitTasksChanged } from "../workspace/_shared";
import TaskRowV2 from "../workspace/panels/TaskRowV2";
import "../workspace/workspace.css";
import { useAuth } from "../../context/AuthContext";

function Card({ icon: Icon, tone, title, count, sub, right, children, testid, def = true }) {
  const [open, setOpen] = useState(def);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testid}>
      <div className="dash-sec-head flex items-center gap-2 px-3 py-2.5">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 flex-1 text-left" data-testid={`${testid}-toggle`}>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
          <Icon className={`w-4 h-4 ${tone}`} /><span className="text-[14.5px] font-semibold text-slate-100">{title}</span>
          <span className="text-[11px] font-mono text-slate-400" data-testid={`${testid}-count`}>{count}</span>
          {sub && <span className="text-[11px] text-slate-500 truncate">{sub}</span>}
        </button>
        {right}
      </div>
      {open && <div className="border-t border-white/[0.06]">{children}</div>}
    </div>
  );
}

const GROUPS = [["Overdue", (d) => d.tone === "red"], ["Today", (d) => d.days === 0], ["This week", (d) => d.days > 0 && d.days <= 7 - (new Date().getDay() || 7)], ["Later & no date", () => true]];

export function useCompanyWork(viewUserId) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [mine, setMine] = useState([]);
  const [pool, setPool] = useState([]);
  const [chip, setChip] = useState("open");
  const load = useCallback(async () => {
    const lens = viewUserId ? { user_id: viewUserId } : {};
    try {
      const [c, u, t, m, p, w] = await Promise.all([workspaceApi.getConfig(), workspaceApi.listUsers(), workspaceApi.listTeams("all"),
        workspaceApi.listTasks({ scope: "mine", open: true, ...lens }), workspaceApi.listTasks({ scope: "claimable", ...lens }),
        chip === "watching" ? workspaceApi.listTasks({ scope: "watching", open: true, ...lens }) : Promise.resolve(null)]);
      setCfg(c); setUsers(u || []); setTeams(t.items || []); setMine((w || m).items || []); setPool(p.items || []);
    } catch { /* company work is best-effort */ }
  }, [viewUserId, chip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener(TASK_CHANGED, load); return () => window.removeEventListener(TASK_CHANGED, load); }, [load]);
  const userMap = useMemo(() => Object.fromEntries(withMe(users, user).map((u) => [u.id, u])), [users, user]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const checkins = Object.values(Object.fromEntries([...mine, ...pool].filter((t) => t.routine).map((t) => [t.id, t])))
    .sort((a, b) => (b.routine.missed - a.routine.missed) || (b.routine.due_now - a.routine.due_now));
  const pending = checkins.filter((t) => t.routine.due_now);
  return { statuses: cfg?.statuses || [], userMap, teamMap, mine: mine.filter((t) => !t.routine), pool: pool.filter((t) => !t.routine), checkins, pending, chip, setChip, load, viewUserId };
}

const rowFor = (cw, onOpenTask) => (t) => <TaskRowV2 key={t.id} compact task={t} statuses={cw.statuses} userMap={cw.userMap} teamMap={cw.teamMap} onOpen={(x) => onOpenTask(x.id)} onChanged={cw.load} />;

export function CheckinsCard({ cw, onOpenTask }) {
  return (
    <Card icon={UserCheck} tone="text-violet-300" title="Routines" count={cw.pending.length} sub="due now" testid="rail-checkins">
      {cw.checkins.length === 0 ? <div className="px-3 py-4 text-[12px] text-slate-500 text-center">No routines. Any repeating task becomes a routine you tick.</div>
        : Object.entries(cw.checkins.reduce((g, t) => { const k = cw.teamMap[t.team_id]?.name || "Personal"; (g[k] = g[k] || []).push(t); return g; }, {})).map(([team, items]) => (
          <div key={team} data-testid={`rail-routines-team-${team.replace(/\W+/g, "-").toLowerCase()}`}>
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-mono text-violet-300/80 flex justify-between"><span>{team}</span><span className="text-slate-500">{items.filter((t) => t.routine.due_now).length} due</span></div>
            {items.map(rowFor(cw, onOpenTask))}
          </div>))}
    </Card>
  );
}

export function MyTasksCard({ cw, onOpenTask, onNewTask }) {
  const { mine, chip, setChip, viewUserId } = cw;
  const shown = chip === "overdue" ? mine.filter((t) => dueInfo(t).tone === "red") : mine;
  const grouped = GROUPS.map(([g, fn], i) => [g, shown.filter((t) => { const d = dueInfo(t); return fn(d) && !GROUPS.slice(0, i).some(([, f2]) => f2(d)); })]).filter(([, r]) => r.length);
  return (
    <Card icon={ListChecks} tone="text-sky-300" title="My tasks" count={mine.length} testid="rail-my-tasks"
      right={<button onClick={onNewTask} className="px-2 py-1 rounded-md border border-white/10 text-[11px] text-slate-200 inline-flex items-center gap-1" data-testid="rail-new-task"><Plus className="w-3 h-3" />Task</button>}>
      <div className="flex items-center gap-1.5 px-3 py-2">
        {[["open", "Open"], ["overdue", "Overdue"], ["watching", "Watching"]].map(([k, l]) => (
          <button key={k} onClick={() => setChip(k)} className={`ws-chip !py-0.5 ${chip === k ? "on" : ""}`} data-testid={`rail-chip-${k}`}>{l}</button>
        ))}
        <Link to={`/workspace/my-tasks?scope=mine${viewUserId ? `&user=${viewUserId}` : ""}`} className="ml-auto text-[12px] text-teal-300 hover:underline" data-testid="rail-open-workspace">Open in Workspace →</Link>
      </div>
      {grouped.length === 0 && <div className="px-3 py-4 text-[12px] text-slate-500 text-center">Nothing open.</div>}
      {grouped.map(([g, rows]) => <div key={g}><div className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest ${g === "Overdue" ? "text-rose-300" : "text-slate-500"}`}>{g} · {rows.length}</div>{rows.map(rowFor(cw, onOpenTask))}</div>)}
    </Card>
  );
}

export function TeamPoolCard({ cw, onOpenTask }) {
  return (
    <Card icon={Hand} tone="text-amber-300" title="Team pool" count={cw.pool.length} sub="claimable in your teams" testid="rail-team-pool">
      {cw.pool.length === 0 ? <div className="px-3 py-4 text-[12px] text-slate-500 text-center">Nothing to claim.</div> : cw.pool.map(rowFor(cw, onOpenTask))}
    </Card>
  );
}
